import React, { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    getRentalTerms,
    createRentalTerm,
} from "../rentalService";
import Toster from "../../../common/Toster";
import TinyEditor from "../../../Editor/TinyEditor";
import {
    normalizeQuillHtml,
    quillPlainTextLength,
} from "../rentalTermsUtils";

const RentalTerms = ({ rentalId, registerSave, onValidityChange }) => {
    const queryClient = useQueryClient();
    const [content, setContent] = useState("");
    const [isHydrated, setIsHydrated] = useState(false);
    const [showToster, setShowToster] = useState(false);
    const [tosterMessage, setTosterMessage] = useState("");
    const [tosterType, setTosterType] = useState("success");

    const { data: termsData, isLoading } = useQuery({
        queryKey: ["rentalTerms", rentalId],
        queryFn: () => getRentalTerms(rentalId),
        enabled: !!rentalId,
    });

    const serverContent = normalizeQuillHtml(termsData?.data?.content ?? "");
    const editorValue = content || serverContent;

    useEffect(() => {
        if (isLoading) return;
        setContent(normalizeQuillHtml(termsData?.data?.content ?? ""));
        setIsHydrated(true);
    }, [termsData, isLoading, rentalId]);

    const showToast = (type, message) => {
        setTosterType(type);
        setTosterMessage(message);
        setShowToster(true);
    };

    const saveMutation = useMutation({
        mutationFn: createRentalTerm,
        onSuccess: () => {
            queryClient.invalidateQueries(["rentalTerms", rentalId]);
            showToast("success", "Terms & Conditions saved.");
        },
        onError: (error) => {
            showToast("error", error.response?.data?.message || "Failed to save terms");
        }
    });

    const handleSave = async () => {
        const normalized = normalizeQuillHtml(content);
        if (quillPlainTextLength(normalized) < 10) {
            showToast("error", "Terms must be at least 10 characters of text (excluding empty formatting).");
            throw new Error("Validation failed");
        }
        if (normalized.length > 65535) {
            showToast("error", "Terms are too long (max 65535 characters including formatting).");
            throw new Error("Validation failed");
        }
        return saveMutation.mutateAsync({
            rental_item_id: rentalId,
            content: normalized,
        });
    };

    useEffect(() => {
        registerSave?.(() => handleSave());
    }, [registerSave, content, rentalId]);

    useEffect(() => {
        const ok =
            quillPlainTextLength(content) >= 10 &&
            normalizeQuillHtml(content).length <= 65535;
        onValidityChange?.(ok);
    }, [content, onValidityChange]);

    if (isLoading) {
        return (
            <div className="p-10 text-center text-gray-400 font-medium">
                Loading terms...
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="rounded-xl border border-amber-100 bg-amber-50/60 px-4 py-3 flex gap-3">
                <i className="fi fi-rr-info text-amber-600 mt-0.5 shrink-0" aria-hidden />
                <p className="text-xs text-amber-900/80 leading-relaxed">
                    Optional step — add terms and conditions for this rental. Use{" "}
                    <span className="font-semibold">Skip & Finish</span> at the bottom to complete setup without terms.
                </p>
            </div>

            <div className="rounded-xl border border-gray-100 bg-slate-50/40 overflow-hidden">
                <div className="px-5 py-2 border-b border-gray-100 flex justify-between items-center">
                    <span className="text-xs font-medium text-gray-500">Policy content</span>
                    <span className="text-xs text-gray-400">
                        {quillPlainTextLength(editorValue)} characters
                    </span>
                </div>
                <div className="p-5">
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                        <TinyEditor
                            key={`rental-terms-${rentalId}-${isHydrated ? "ready" : "loading"}`}
                            value={editorValue}
                            onChange={(v) => setContent(v)}
                            height={360}
                        />
                    </div>
                </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 flex gap-3 italic">
                <i className="fi fi-rr-info text-blue-500 mt-0.5"></i>
                <p className="text-xs text-blue-700 leading-relaxed">
                    <strong>Pro Tip:</strong> Clearly stating your refund policy, damage fees, and identification requirements helps prevent disputes and build trust with your customers.
                </p>
            </div>

            {showToster && (
                <Toster
                    message={tosterMessage}
                    type={tosterType}
                    onClose={() => setShowToster(false)}
                />
            )}
        </div>
    );
};

export default RentalTerms;
